/**
 * AccessibleModulesCard
 *
 * "What does this access provide?" card: lists the modules a role grants edit / view-only access to.
 * Shared by the Submit Ticket access-request form and the Ticket Detail screen.
 */
import React, { useEffect, useMemo, useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { AccessibleModuleItem, getAccessibleModules } from "../../../api/ticketApi";

type Props = {
  roleId: number | null;
  roleName?: string;
  /** Role has global scope, so access is the role's default configuration */
  isFullScope?: boolean;
  /** Shown for Temporary Access requests */
  endDate?: Date | string | null;
};

const SKELETON_COUNT = 4;

const AccessibleModulesCard: React.FC<Props> = ({ roleId, roleName, isFullScope, endDate }) => {
  const [modules, setModules] = useState<AccessibleModuleItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (roleId == null || !Number.isFinite(roleId)) {
      setModules([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getAccessibleModules(roleId)
      .then((items) => { if (!cancelled) setModules(items); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [roleId]);

  const visible = useMemo(() => {
    const seen = new Set<string>();
    return modules
      .filter((m) => {
        if (!m.hasAccess) return false;
        const key = `${m.moduleId}:${m.moduleName.trim().toLowerCase()}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [modules]);

  // readOnly is missing on some records — default to view-only.
  const fullAccess = visible.filter((m) => m.readOnly === false);
  const viewOnly = visible.filter((m) => m.readOnly !== false);

  const endDateText =
    endDate &&
    new Date(endDate).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });

  return (
    <View style={s.card}>
      <View style={s.header}>
        <MaterialIcons name="info-outline" size={16} color="#1d4ed8" />
        <View style={{ flex: 1, marginLeft: 8 }}>
          <Text style={s.title}>What does this access provide?</Text>
          <Text style={s.subtitle}>From the Config module's role privileges for the selected site(s)</Text>
        </View>
      </View>
      <View style={s.badge}>
        <Text style={s.badgeText}>{isFullScope ? "DEFAULT CONFIGURATION" : "SAME ACCESS AT ALL SELECTED SITES"}</Text>
      </View>
      <Text style={s.label}>
        {isFullScope ? (
          `Based on the default ${roleName || "role"} configuration`
        ) : (
          <>With this role, the user will have <Text style={s.bold}>EDIT</Text> access to:</>
        )}
      </Text>
      <View style={s.pills}>
        {loading ? (
          Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <View key={i} style={[s.pill, s.pillLoading]}><View style={s.skeleton} /></View>
          ))
        ) : fullAccess.length > 0 ? (
          fullAccess.map((m) => (
            <View key={m.accessId} style={s.pill}><Text style={s.pillText}>{m.moduleName}</Text></View>
          ))
        ) : visible.length === 0 ? (
          <Text style={s.label}>Select a role to view accessible modules.</Text>
        ) : null}
      </View>
      {!loading && viewOnly.length > 0 ? (
        <>
          <Text style={[s.label, { marginTop: 12 }]}>
            With this role, the user will have <Text style={s.bold}>VIEW ONLY</Text> access to:
          </Text>
          <View style={s.pills}>
            {viewOnly.map((m) => (
              <View key={m.accessId} style={s.pill}><Text style={s.pillText}>{m.moduleName}</Text></View>
            ))}
          </View>
        </>
      ) : null}
      {endDateText ? (
        <Text style={s.endNote}>
          Temporary access ends <Text style={s.bold}>{endDateText}</Text>, when access reverts automatically to the user's current role.
        </Text>
      ) : null}
    </View>
  );
};

const s = StyleSheet.create({
  card: {
    backgroundColor: "#eff6ff",
    borderWidth: 1,
    borderColor: "#bfdbfe",
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 12,
    marginTop: 16,
    width: "100%",
  },
  header: { flexDirection: "row", alignItems: "flex-start", marginBottom: 10 },
  title: { fontSize: 13, fontWeight: "700", color: "#1e3a8a" },
  subtitle: { fontSize: 11, color: "#3b82f6", marginTop: 2 },
  badge: {
    alignSelf: "flex-start",
    backgroundColor: "#dcfce7",
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 3,
    marginBottom: 10,
  },
  badgeText: { fontSize: 11, fontWeight: "700", color: "#15803d", letterSpacing: 0.4 },
  label: { fontSize: 12, color: "#374151", marginBottom: 8 },
  bold: { fontWeight: "700" },
  pills: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
  pill: {
    borderWidth: 1,
    borderColor: "#93c5fd",
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: "#ffffff",
    maxWidth: "100%",
  },
  pillText: { fontSize: 13, color: "#1e40af", fontWeight: "600", flexShrink: 1 },
  pillLoading: { paddingHorizontal: 0, paddingVertical: 0, backgroundColor: "#eff6ff" },
  skeleton: { width: 92, height: 18, borderRadius: 999, backgroundColor: "#dbeafe" },
  endNote: {
    marginTop: 10,
    fontSize: 12,
    color: "#374151",
    lineHeight: 18,
    borderTopWidth: 1,
    borderTopColor: "#bfdbfe",
    paddingTop: 8,
  },
});

export default AccessibleModulesCard;
